import React, { useEffect } from 'react';
import Moment from 'moment';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { translate, Spinner } from '@haldor/ui';
import { Box, Chip, Typography, ButtonBase } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getLeaveRequests } from 'actions/absence';
import LeaveRequest from './class/LeaveRequest';

// Centralized style helpers to keep JSX lean
var styles = {
  loading: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    py: 2,
    px: 1
  },
  empty: {
    py: 1,
    px: 1
  },
  statusDateRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    mb: 0,
    gap: 1
  },
  statusCluster: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    justifyContent: 'flex-end'
  },
  dateText: {
    fontWeight: 600,
    fontSize: '.8125rem',
    lineHeight: 1.25,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    textAlign: 'left'
  },
  reason: {
    fontSize: '.68rem',
    color: 'text.secondary',
    letterSpacing: '.25px',
    flexGrow: 1,
    textAlign: 'left',
    minWidth: 0,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  chevron: {
    fontSize: 24,
    color: 'text.secondary'
  },
  card: {
    position: 'relative',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'center',
    width: '100%',
    border: '1px solid #D8D8D8',
    borderRadius: 1,
    p: 2,
    minHeight: 72,
    mb: 1.25,
    backgroundColor: '#FFFFFF',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    cursor: 'pointer',
    transition: 'border-color .15s, box-shadow .15s, background .15s',
    '&:hover': {
      backgroundColor: '#F9FCFF',
      borderColor: '#90caf9',
      boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
    },
    '&:focus-visible': {
      outline: 'none',
      boxShadow: '0 0 0 3px rgba(25,118,210,0.35)',
      borderColor: '#1976d2'
    },
    '&:active': {
      transform: 'scale(.985)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.18)'
    }
  }
};
var statusChipProps = function statusChipProps(status) {
  if (!status) return {
    label: 'Pending',
    sx: {
      backgroundColor: '#FFA726',
      color: '#FFFFFF'
    }
  };
  var s = status.toLowerCase();
  if (s.includes('approved')) return {
    label: status,
    sx: {
      backgroundColor: '#4BAF4D',
      color: '#FFFFFF',
      borderRadius: 1
    }
  };
  if (s.includes('reject')) return {
    label: status,
    sx: {
      backgroundColor: '#DF2D2E',
      color: '#FFFFFF',
      borderRadius: 1
    }
  };
  if (s.includes('pending')) return {
    label: status,
    sx: {
      backgroundColor: '#ffffff',
      color: 'text.primary',
      border: '1px solid #d0d0d0',
      borderRadius: 1
    }
  };
  return {
    label: status,
    sx: {
      backgroundColor: '#757575',
      color: '#FFFFFF',
      borderRadius: 1
    }
  };
};

/*
 * LeaveApplicationCardList (simplified original version)
 * Card/list presentation of leave applications (formerly LeaveApplicationTable)
 * Props:
 *  - forms: Array from Redux (all forms)
 *  - loading: boolean
 *  - onClick(application)
 */
var LeaveApplicationCardList = function LeaveApplicationCardList(_ref) {
  var _ref$loading = _ref.loading,
    loading = _ref$loading === void 0 ? false : _ref$loading,
    onClick = _ref.onClick,
    translate = _ref.translate,
    getLeaveRequests = _ref.getLeaveRequests,
    leaveRequests = _ref.leaveRequests;
  // Fetch forms on mount with LEAVE_REQUEST type filter
  useEffect(function () {
    if (leaveRequests === null) {
      // Fetch forms from 6 months ago to 6 months in the future
      var startDate = Moment().subtract(6, 'months').format('YYYY-MM-DD');
      var endDate = Moment().add(6, 'months').format('YYYY-MM-DD');
      getLeaveRequests(startDate, endDate);
    }
  }, [getLeaveRequests, leaveRequests]);

  // Transform forms into leave application cards using LeaveRequest class
  var applications = React.useMemo(function () {
    if (!leaveRequests || !Array.isArray(leaveRequests) || leaveRequests.length === 0) return [];
    var mapped = LeaveRequest.toTableRows(leaveRequests) || [];
    var getCreated = function getCreated(item) {
      if (!item) return 0;
      if (item.receivedDate) return new Date(item.receivedDate).getTime();
      return 0;
    };
    mapped.sort(function (a, b) {
      return getCreated(b) - getCreated(a);
    });
    return mapped;
  }, [leaveRequests]);
  var rows = React.useMemo(function () {
    return (applications || []).map(function (a, idx) {
      var start = Moment.utc(a.startTime).local();
      var end = Moment.utc(a.endTime).local();
      var startStr = start.format('YYYY-MM-DD');
      var endStr = end.format('YYYY-MM-DD');
      var sameDay = startStr === endStr;
      return {
        id: a.id || "leave_".concat(idx, "_").concat(a.startTime, "_").concat(a.endTime),
        dateRangeDisplay: sameDay ? startStr : "".concat(startStr, " \u2013 ").concat(endStr),
        reason: a.reason,
        status: a.status || 'Pending',
        _original: a
      };
    });
  }, [applications]);

  // Show loader if leave requests data hasn't been loaded yet (null/undefined)
  if (leaveRequests === null) {
    return /*#__PURE__*/React.createElement(Box, {
      sx: styles.loading
    }, /*#__PURE__*/React.createElement(Spinner, {
      center: true
    }));
  }

  // Only show "no applications" message when data has been loaded but result is empty
  if (!rows.length) {
    return /*#__PURE__*/React.createElement(Box, {
      sx: styles.empty
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "text.secondary"
    }, translate('No leave applications found')));
  }
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: '100%'
    }
  }, rows.map(function (r) {
    var chipProps = statusChipProps(r.status);
    var handleActivate = function handleActivate() {
      if (onClick) onClick(r._original || r);
    };
    return /*#__PURE__*/React.createElement(ButtonBase, {
      key: r.id,
      focusRipple: true,
      onClick: handleActivate,
      "data-leave-id": r.id,
      sx: styles.card
    }, /*#__PURE__*/React.createElement(Box, {
      sx: styles.statusDateRow
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      sx: styles.dateText
    }, r.dateRangeDisplay), /*#__PURE__*/React.createElement(Box, {
      sx: styles.statusCluster
    }, /*#__PURE__*/React.createElement(Chip, {
      size: "small",
      label: translate(chipProps.label),
      sx: chipProps.sx
    }), /*#__PURE__*/React.createElement(ChevronRightIcon, {
      sx: styles.chevron
    }))));
  }));
};
LeaveApplicationCardList.propTypes = {
  forms: PropTypes.array,
  leaveRequests: PropTypes.array,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  translate: PropTypes.func.isRequired,
  getLeaveRequests: PropTypes.func.isRequired
};
function mapStateToProps(state) {
  return {
    translate: translate(state.Languages.translations),
    forms: state.Templates.forms,
    leaveRequests: state.absence.leaveRequests,
    loading: state.Templates.loading || false
  };
}
export default connect(mapStateToProps, {
  getLeaveRequests: getLeaveRequests
})(LeaveApplicationCardList);